// Reports — the Phase 3 surface (docs/SPEC-001.md §4, §12; INV-4, INV-9).
//
// Reads-only from the normalized store (store.ts): this component never
// calls fetch, exactly like client/src/pages/Tasks.tsx. INV-1 ("navigation =
// 0 network calls") means loading report/work-item data is someone else's
// job at hydrate time, not something this page does when it mounts.
//
// HONEST GAP, named rather than hidden (same shape as Tasks.tsx's gap):
// nothing in this repo yet writes rows into the store under the
// `reportDefinition` / `reportRun` / `reportRow` / `workItem` entity types
// this page reads. That bootstrap wiring belongs in client/src/main.tsx /
// app.tsx, both off-limits to this task (another agent owns them, and this
// task's file scope is client/src/pages/ + client/tests/ only — no
// client/src/lib/ files may be added). This page defines the CONTRACT it
// expects and renders a named, visible empty state until something
// populates it, never a blank page (INV-9):
//
//   state.value.reportDefinition = { [id]: ReportDefinitionRow }  — one row
//     per src/reports.js's GET /api/v1/reports `reports[]` entry.
//   state.value.reportRun        = { [id]: ReportRunRow }         — rows
//     from GET /api/v1/reports/:id/runs, each carrying its own
//     reportDefinitionId so this page can filter by definition.
//   state.value.reportRow        = { [id]: ReportRowRow }         — rows
//     from GET /api/v1/reports/runs/:runId/rows, each carrying its own
//     reportRunId so this page can filter by run.
//   state.value.workItem         = { [id]: WorkItemRow }          — rows
//     from GET /api/v1/work-items, unchanged shape.
//
// COMPARISON, computed client-side rather than fetched: SPEC-001 §4 point 3
// wants run-to-run comparison as a first-class view. The server endpoint
// (GET /api/v1/reports/compare) exists in src/reports.js, but calling it from
// here would be a fetch triggered by a user selection, and — same file-scope
// constraint as above — there is nowhere under this task's allowed paths to
// put an API-calling module. Instead, once both selected runs' rows are
// already present in the store (e.g. because the user opened each run's
// Rows view first), the comparison is computed locally from
// `clientDiffRuns` below: the SAME algorithm as src/portal_diff.js
// (num() coercion, the 0.005 threshold, the all-zero appeared/resolved
// filter), duplicated here in TypeScript because that CommonJS file cannot
// be imported into the Vite/ESM client bundle without build config this task
// may not touch. A follow-up should factor this into client/src/lib/ once a
// session with that scope is doing the wiring, so there is exactly one copy
// of the algorithm instead of two.
import type { JSX } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import { useTableSort } from '../lib/tableSort';
import { SortableTh } from '../components/SortableTh';
import { state } from '../lib/store';
import { orderLocations, LOCATION_NAMES, type LocationCode } from '../lib/locations';
import { formatDate } from '../lib/date';
import { FaultState, EmptyState } from '../components/FaultState';
import { ClinicTabs, visibleClinics, activeClinicFrom, MANAGEMENT_TAB_ID, EXPECTED_REPORTS_TAB_ID, canSeeManagementTab } from '../components/ClinicTabs';
import { ManagementPlaceholder, SHOW_EMPTY_MANAGEMENT_TABS } from '../components/ManagementPlaceholder';
import { FavoriteStar } from '../components/FavoriteStar';
import { ReportBoard } from '../components/ReportBoard';
import { ReportScheduleBoard } from '../components/ReportScheduleBoard';

// --- store contract types ----------------------------------------------------

export interface RunProvenance {
  id: number;
  reportDefinitionId?: number | null;
  periodStart: string | null;
  periodEnd: string | null;
  pulledAt: string | null;
  pulledBy: string | null;
  sourceFileUrl: string | null;
  rowCount: number | null;
  checksum: string | null;
  status: string | null;
}

export interface ReportDefinitionRow {
  id: number;
  name: string;
  sourceSystem: string;
  cadence: 'WEEKLY' | 'DAILY' | 'MONTHLY' | 'AD_HOC';
  taskDefinitionId: number | null;
  rowKey: string;
  materialColumns: string[];
  automated: boolean;
  latestRun: RunProvenance | null;
}

export type ReportRunRow = RunProvenance & { reportDefinitionId: number };

export interface ReportRowRow {
  id: number;
  reportRunId: number;
  entityKey: string;
  fields: Record<string, unknown>;
  createdAt: string;
}

export interface WorkItemRow {
  id: number;
  entityKey: string;
  locationCode: string;
  assignee: string | null;
  attempts: number;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'ESCALATED' | 'DROPPED';
  outcome: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  agingDays: number;
  source: {
    reportRowId: number;
    reportRunId: number | null;
    reportDefinitionId: number | null;
    sourceFileUrl: string | null;
    pulledAt: string | null;
  } | null;
}

function getTable<T>(entityType: string): Record<string, T> {
  return (state.value[entityType] ?? {}) as Record<string, T>;
}

// --- client-side comparison (SAME algorithm as src/portal_diff.js) ----------
//
// See the module header above for why this is duplicated here rather than
// imported. Any change to src/portal_diff.js's semantics must be mirrored
// here by hand until the two are unified — client/tests/reports-page.test.tsx
// asserts this copy against the same fixtures src/reports.test.js uses, so a
// drift between the two shows up as a test failure, not silently.

function num(raw: unknown): number {
  if (!raw && raw !== 0) return 0;
  let s = String(raw).replace(/,/g, '').replace(/\$/g, '').trim();
  if (!s || s === '-') return 0;
  if (s.endsWith('%')) s = s.slice(0, -1);
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function materialSnapshot(fields: Record<string, unknown>, materialColumns: string[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const c of materialColumns) out[c] = num(fields ? fields[c] : undefined);
  return out;
}

function anyMaterial(snap: Record<string, number>, materialColumns: string[]): boolean {
  return materialColumns.some((c) => snap[c] !== 0);
}

export interface DiffResult {
  changed: { entityKey: string; before: Record<string, number>; after: Record<string, number>; delta: Record<string, number> }[];
  appeared: { entityKey: string; fields: Record<string, number> }[];
  resolved: { entityKey: string; fields: Record<string, number> }[];
}

/** The client-side twin of src/portal_diff.js's diffRuns — see module header. */
export function clientDiffRuns(
  oldRows: { entityKey: string; fields: Record<string, unknown> }[],
  newRows: { entityKey: string; fields: Record<string, unknown> }[],
  materialColumns: string[]
): DiffResult {
  if (!materialColumns || materialColumns.length === 0) {
    throw new Error('clientDiffRuns: materialColumns must be a non-empty array');
  }
  const oldByKey = new Map(oldRows.map((r) => [r.entityKey, r.fields || {}]));
  const newByKey = new Map(newRows.map((r) => [r.entityKey, r.fields || {}]));

  const changed: DiffResult['changed'] = [];
  const appeared: DiffResult['appeared'] = [];
  for (const key of [...newByKey.keys()].sort()) {
    const newSnap = materialSnapshot(newByKey.get(key)!, materialColumns);
    if (!oldByKey.has(key)) {
      if (anyMaterial(newSnap, materialColumns)) appeared.push({ entityKey: key, fields: newSnap });
      continue;
    }
    const oldSnap = materialSnapshot(oldByKey.get(key)!, materialColumns);
    const delta: Record<string, number> = {};
    for (const c of materialColumns) {
      const d = newSnap[c] - oldSnap[c];
      if (Math.abs(d) > 0.005) delta[c] = Math.round(d * 100) / 100;
    }
    if (Object.keys(delta).length > 0) changed.push({ entityKey: key, before: oldSnap, after: newSnap, delta });
  }

  const resolved: DiffResult['resolved'] = [];
  for (const key of [...oldByKey.keys()].sort()) {
    if (newByKey.has(key)) continue;
    const oldSnap = materialSnapshot(oldByKey.get(key)!, materialColumns);
    if (anyMaterial(oldSnap, materialColumns)) resolved.push({ entityKey: key, fields: oldSnap });
  }
  return { changed, appeared, resolved };
}

// --- provenance (INV-4: every figure traces to a named source, one click away) --

/** A run's provenance line — the source file link, row count and checksum,
 * one click away from any number pulled out of it. Never renders a number
 * without this alongside it (standing rule 9/14). */
function RunProvenanceLine({ run }: { run: RunProvenance | null }): JSX.Element {
  if (!run) {
    return <span style={{ color: 'var(--color-text-muted)' }}>No run yet — nothing to trace this to.</span>;
  }
  return (
    <span data-testid="run-provenance">
      Run #{run.id}
      {run.pulledAt && (
        <>
          {' · '}
          {formatDate(run.pulledAt)}
        </>
      )}
      {' · '}
      {run.rowCount ?? 0} rows
      {run.checksum && (
        <>
          {' · checksum '}
          <code>{run.checksum}</code>
        </>
      )}
      {run.sourceFileUrl ? (
        <>
          {' · '}
          <a href={run.sourceFileUrl} target="_blank" rel="noreferrer">
            source file
          </a>
        </>
      ) : (
        <>
          {' · '}
          <span style={{ color: 'var(--color-text-muted)' }}>no source file on this run</span>
        </>
      )}
    </span>
  );
}

// --- the page -----------------------------------------------------------------


// ---------------------------------------------------------------------------
// The shelf — what the live app's Reports page actually IS.
//
// Ground truth: the deployed board (docs/APPS-SCRIPT-MAP.md, "Reports"): "A
// per-location shelf of pulled reports, grouped by source_system
// (Meevo/ADP/ME Portal/Tippy/'Unspecified source'), each showing Period/Date
// and the task it drives", and nothing on it is writable.
//
// What this page WAS is a run-comparison tool — Phase 3's own subject, a
// different product wearing the same name. It is kept, below the shelf and
// folded away, because it works and it is the only place run-to-run diffing
// lives.
// ---------------------------------------------------------------------------

export interface ShelfEntry {
  locationCode: string;
  periodStart: string | null;
  periodEnd: string | null;
  pulledAt: string | null;
  pulledBy: string | null;
  sourceFileUrl: string | null;
  status: string | null;
  rowCount: number | null;
}

/** Source systems in the order the live app groups them, then anything else.
 * An unrecognised system is shown under its own name — never silently merged
 * into "Other", which would hide that a new puller appeared. */
const SOURCE_ORDER = ['MEEVO', 'ADP', 'ME_PORTAL', 'TIPPY', 'MA060', 'MONDAY', 'OTHER'];

const SOURCE_LABEL: Record<string, string> = {
  MEEVO: 'Meevo',
  ADP: 'ADP',
  ME_PORTAL: 'ME Portal',
  TIPPY: 'Tippy',
  MA060: 'MA060',
  MONDAY: 'Monday',
  OTHER: 'Unspecified source',
};

function sourceLabel(code: string): string {
  return SOURCE_LABEL[code] ?? code;
}

function ReportShelf(
  { definitions, shelf, clinic, taskNameFor }:
  {
    definitions: ReportDefinitionRow[];
    shelf: Record<string, Record<string, ShelfEntry>>;
    clinic: string;
    taskNameFor: (id: number | null) => string | null;
  },
): JSX.Element {
  const groups = new Map<string, ReportDefinitionRow[]>();
  for (const def of definitions) {
    const key = def.sourceSystem || 'OTHER';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(def);
  }
  const ordered = [...groups.keys()].sort((a, b) => {
    const ia = SOURCE_ORDER.indexOf(a);
    const ib = SOURCE_ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
  });

  if (!definitions.length) return <EmptyState label="No reports defined yet." />;

  return (
    <>
      {ordered.map((source) => (
        <SourceCard
          key={source}
          source={source}
          definitions={groups.get(source)!}
          shelf={shelf}
          clinic={clinic}
          taskNameFor={taskNameFor}
        />
      ))}
    </>
  );
}

/** One source's reports. Its own component so the sort hook has somewhere to
 * live — a hook cannot run inside a `.map()` callback. */
function SourceCard({ source, definitions, shelf, clinic, taskNameFor }: {
  source: string;
  definitions: ReportDefinitionRow[];
  shelf: Record<string, Record<string, ShelfEntry>>;
  clinic: string;
  taskNameFor: (id: number | null) => string | null;
}): JSX.Element {
  const sorted = useTableSort(definitions, {
    report: (def) => def.name,
    period: (def) => shelf[String(def.id)]?.[clinic]?.periodStart ?? null,
    pulled: (def) => shelf[String(def.id)]?.[clinic]?.pulledAt ?? null,
  });
  return (
        <section class="card">
          <div class="section-head">
            <h2>{sourceLabel(source)}</h2>
            <p>{definitions.length} report{definitions.length === 1 ? '' : 's'} from this source.</p>
          </div>
          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <SortableTh label="Report" sortKey="report" state={sorted.state} onSort={sorted.toggle} />
                  <SortableTh label="Period" sortKey="period" state={sorted.state} onSort={sorted.toggle} />
                  <SortableTh label="Pulled" sortKey="pulled" state={sorted.state} onSort={sorted.toggle} />
                  <th>Drives</th>
                  <th>File</th>
                </tr>
              </thead>
              <tbody>
                {sorted.rows.map((def) => {
                  const entry = shelf[String(def.id)]?.[clinic] ?? null;
                  const task = taskNameFor(def.taskDefinitionId ?? null);
                  return (
                    <tr key={def.id}>
                      <td>
                        <div class="tk">{def.name}</div>
                        <div class="meta">{def.cadence}</div>
                      </td>
                      <td>
                        {entry?.periodStart
                          ? `${formatDate(entry.periodStart)}${entry.periodEnd ? ` – ${formatDate(entry.periodEnd)}` : ''}`
                          : NOT_PULLED}
                      </td>
                      <td>{entry?.pulledAt ? formatDate(entry.pulledAt) : NOT_PULLED}</td>
                      <td>{task ?? NOT_PULLED}</td>
                      <td>
                        {entry?.sourceFileUrl
                          ? <a href={entry.sourceFileUrl} target="_blank" rel="noopener noreferrer">Open</a>
                          : NOT_PULLED}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
  );
}

// A report never pulled for this clinic is a real, nameable state and must not
// look like a zero or a blank (INV-9).
const NOT_PULLED = 'Not pulled for this clinic';

/**
 * Reports is a tab inside Company now (owner, 2026-10-06: "Id like to move
 * reports and Money under company as a sub menu item"). Company passes
 * `embeddedClinic` — the clinic picked on Company's own strip — and the page
 * drops its own frame (`.main`, the "Reports" heading, its clinic strip). The
 * "Expected reports" view that used to ride on that strip becomes the second
 * of two buttons, Reports / Expected reports, in Company's menu style.
 * Without the prop it is the standalone page it always was.
 */
export function ReportsPage({ embeddedClinic }: { embeddedClinic?: LocationCode } = {}): JSX.Element {
  const embedded = embeddedClinic !== undefined;
  const [clinic, setClinic] = useState<string | null>(null);
  const [extraTab, setExtraTab] = useState<string | null>(null);
  let definitions: ReportDefinitionRow[];
  let runs: ReportRunRow[];
  let rows: ReportRowRow[];
  let workItems: WorkItemRow[];
  try {
    definitions = Object.values(getTable<ReportDefinitionRow>('reportDefinition'));
    runs = Object.values(getTable<ReportRunRow>('reportRun'));
    rows = Object.values(getTable<ReportRowRow>('reportRow'));
    workItems = Object.values(getTable<WorkItemRow>('workItem'));
  } catch (err) {
    const fault = <FaultState name="report data unreadable" detail={err instanceof Error ? err.message : String(err)} />;
    if (embedded) return fault;
    return (
      <div class="main">
        <div class="page-header"><h1>Reports</h1></div>
        {fault}
      </div>
    );
  }

  const [selectedDefId, setSelectedDefId] = useState<number | null>(null);
  const [fromRunId, setFromRunId] = useState<number | null>(null);
  const [toRunId, setToRunId] = useState<number | null>(null);

  const sortedDefinitions = useMemo(
    () => [...definitions].sort((a, b) => a.name.localeCompare(b.name)),
    [definitions]
  );

  // Click-to-sort on the definitions list (owner, 2026-08-19). Alphabetical
  // stays the default, so the row the page selects on load does not move.
  const defSort = useTableSort(sortedDefinitions, {
    report: (d) => d.name,
    source: (d) => d.sourceSystem,
    cadence: (d) => d.cadence,
    automated: (d) => Boolean(d.automated),
    latest: (d) => d.latestRun?.pulledAt ?? null,
  });

  const activeDefId = selectedDefId ?? sortedDefinitions[0]?.id ?? null;
  const activeDef = sortedDefinitions.find((d) => d.id === activeDefId) ?? null;

  const runsForDef = useMemo(
    () =>
      runs
        .filter((r) => r.reportDefinitionId === activeDefId)
        .sort((a, b) => String(b.pulledAt ?? '').localeCompare(String(a.pulledAt ?? ''))),
    [runs, activeDefId]
  );

  const fromRun = runsForDef.find((r) => r.id === fromRunId) ?? null;
  const toRun = runsForDef.find((r) => r.id === toRunId) ?? null;

  const comparison = useMemo(() => {
    if (!activeDef || !fromRun || !toRun) return null;
    const fromRows = rows.filter((r) => r.reportRunId === fromRun.id).map((r) => ({ entityKey: r.entityKey, fields: r.fields }));
    const toRows = rows.filter((r) => r.reportRunId === toRun.id).map((r) => ({ entityKey: r.entityKey, fields: r.fields }));
    // Both runs' rows must already be in the store — see the module header
    // on why this page never fetches them itself.
    if (fromRows.length === 0 && toRows.length === 0) return null;
    return clientDiffRuns(fromRows, toRows, activeDef.materialColumns);
  }, [rows, activeDef, fromRun, toRun]);

  const workItemsByLocation = useMemo(() => {
    const byLoc = new Map<string, WorkItemRow[]>();
    for (const wi of workItems) {
      if (!byLoc.has(wi.locationCode)) byLoc.set(wi.locationCode, []);
      byLoc.get(wi.locationCode)!.push(wi);
    }
    for (const list of byLoc.values()) list.sort((a, b) => b.agingDays - a.agingDays);
    return byLoc;
  }, [workItems]);
  const locationsPresent = orderLocations([...workItemsByLocation.keys()] as LocationCode[]);

  const clinics = visibleClinics();
  const activeClinic = embeddedClinic ?? activeClinicFrom(clinic, clinics);
  const shelf = getTable<Record<string, ShelfEntry>>('reportShelf') as unknown as
    Record<string, Record<string, ShelfEntry>>;
  const taskNameFor = (id: number | null): string | null => {
    if (id === null) return null;
    const tasks = getTable<{ task_definition_id: number; definition_name: string }>('task');
    const hit = Object.values(tasks).find((t) => t.task_definition_id === id);
    return hit ? hit.definition_name : null;
  };

  const board = (
        <>
      <ReportBoard clinic={activeClinic} />

      {(sortedDefinitions.length > 0 || workItems.length > 0) && (
      <details class="migrated">
      <summary>Pulled reports, run comparison, and work items</summary>
      <ReportShelf
        definitions={sortedDefinitions}
        shelf={shelf}
        clinic={activeClinic}
        taskNameFor={taskNameFor}
      />

      {/* The run-comparison tool this page used to BE. Folded away, the same
          way the live Money page folds its migrated records: it is real work
          and the only place run-to-run diffing lives, but it is not what a
          manager opens Reports for. */}
      <section style={{ marginBottom: 'var(--space-6)' }}>
        <h2>Definitions</h2>
        {sortedDefinitions.length === 0 ? (
          <EmptyState label="No report definitions loaded yet." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="report-definitions" style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <SortableTh label="Report" sortKey="report" state={defSort.state} onSort={defSort.toggle} />
                  <SortableTh label="Source" sortKey="source" state={defSort.state} onSort={defSort.toggle} />
                  <SortableTh label="Cadence" sortKey="cadence" state={defSort.state} onSort={defSort.toggle} />
                  <SortableTh label="Automated" sortKey="automated" state={defSort.state} onSort={defSort.toggle} />
                  <SortableTh label="Latest run" sortKey="latest" state={defSort.state} onSort={defSort.toggle} />
                </tr>
              </thead>
              <tbody>
                {defSort.rows.map((def) => (
                  <tr
                    key={def.id}
                    onClick={() => setSelectedDefId(def.id)}
                    style={{
                      cursor: 'pointer',
                      background: def.id === activeDefId ? 'var(--color-surface-selected, transparent)' : undefined,
                    }}
                  >
                    <td style={{ textAlign: 'left', padding: 'var(--space-2)', borderBottom: '1px solid var(--color-border)' }}>{def.name}</td>
                    <td style={{ textAlign: 'center', padding: 'var(--space-2)', borderBottom: '1px solid var(--color-border)' }}>{def.sourceSystem}</td>
                    <td style={{ textAlign: 'center', padding: 'var(--space-2)', borderBottom: '1px solid var(--color-border)' }}>{def.cadence}</td>
                    <td style={{ textAlign: 'center', padding: 'var(--space-2)', borderBottom: '1px solid var(--color-border)' }}>
                      {/* automated is DERIVED server-side from an actual puller run —
                          never asserted here; this renders exactly the field the API sent. */}
                      {def.automated ? 'Yes' : 'No'}
                    </td>
                    <td style={{ textAlign: 'center', padding: 'var(--space-2)', borderBottom: '1px solid var(--color-border)' }}>
                      <RunProvenanceLine run={def.latestRun} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {activeDef && (
        <section style={{ marginBottom: 'var(--space-6)' }}>
          <h2>Runs — {activeDef.name}</h2>
          {runsForDef.length === 0 ? (
            <EmptyState label="No runs loaded for this report yet." />
          ) : (
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {runsForDef.map((run) => (
                <li key={run.id} style={{ padding: 'var(--space-2) 0', borderBottom: '1px solid var(--color-border)' }}>
                  <label style={{ marginRight: 'var(--space-3)' }}>
                    from{' '}
                    <input
                      type="radio"
                      name="compare-from"
                      checked={fromRunId === run.id}
                      onChange={() => setFromRunId(run.id)}
                    />
                  </label>
                  <label style={{ marginRight: 'var(--space-3)' }}>
                    to{' '}
                    <input type="radio" name="compare-to" checked={toRunId === run.id} onChange={() => setToRunId(run.id)} />
                  </label>
                  <RunProvenanceLine run={run} />
                </li>
              ))}
            </ul>
          )}

          {fromRun && toRun && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <h3>Comparison</h3>
              {!comparison ? (
                <EmptyState label="Rows for both selected runs are not loaded yet — open each run's rows first." />
              ) : (
                <div data-testid="comparison-result">
                  <div>
                    Comparing <RunProvenanceLine run={fromRun} /> to <RunProvenanceLine run={toRun} />
                  </div>
                  <p>Changed: {comparison.changed.length} · Appeared: {comparison.appeared.length} · Resolved: {comparison.resolved.length}</p>
                  {comparison.changed.length === 0 && comparison.appeared.length === 0 && comparison.resolved.length === 0 && (
                    <EmptyState label="No changes between these two runs." />
                  )}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      <section>
        <h2>Work items</h2>
        {locationsPresent.length === 0 ? (
          <EmptyState label="No work items loaded yet." />
        ) : (
          locationsPresent.map((code) => (
            <div key={code} style={{ marginBottom: 'var(--space-5)' }}>
              <h3>{LOCATION_NAMES[code] ?? code}</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {workItemsByLocation.get(code)!.map((wi) => (
                  <li key={wi.id} style={{ padding: 'var(--space-2) 0', borderBottom: '1px solid var(--color-border)' }}>
                    <strong>{wi.entityKey}</strong>
                    {' · '}
                    {wi.status}
                    {' · '}
                    {wi.assignee ?? 'unassigned'}
                    {' · '}
                    {/* Aging DERIVED from first_seen_at at read time (SPEC-001 §4 point
                        4) — this number came straight from the API response, never
                        computed or cached client-side. */}
                    {wi.agingDays} day{wi.agingDays === 1 ? '' : 's'} open
                    {' · '}
                    {wi.source ? (
                      <>
                        from Run #{wi.source.reportRunId ?? '—'}
                        {wi.source.sourceFileUrl && (
                          <>
                            {' '}
                            <a href={wi.source.sourceFileUrl} target="_blank" rel="noreferrer">
                              source file
                            </a>
                          </>
                        )}
                      </>
                    ) : (
                      <span style={{ color: 'var(--color-text-muted)' }}>manually created, no report run behind it</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </section>
      </details>
      )}
        </>
  );

  if (embedded) {
    const expected = extraTab === EXPECTED_REPORTS_TAB_ID;
    return (
      <div data-testid="company-reports">
        <div class="company-disks" data-testid="company-reports-menu">
          <div class="section-head-row">
            <h3>Reports</h3>
            <div class="section-head-actions">
              <FavoriteStar itemKey="reports.shelf" />
            </div>
          </div>
          <div class="mgmt-menu" role="group" aria-label="Reports">
            <button
              type="button"
              class="btn"
              aria-pressed={!expected}
              onClick={() => setExtraTab(null)}
              data-testid="company-reports-chip-board"
            >
              Reports
            </button>
            <button
              type="button"
              class="btn"
              aria-pressed={expected}
              onClick={() => setExtraTab(EXPECTED_REPORTS_TAB_ID)}
              data-testid="company-reports-chip-expected"
            >
              Expected reports
            </button>
          </div>
        </div>
        {expected ? <ReportScheduleBoard clinic={activeClinic} /> : board}
      </div>
    );
  }

  return (
    <div class="main">
      <div class="page-header">
        <div class="section-head-row">
          <h1>Reports</h1>
          <div class="section-head-actions">
            <FavoriteStar itemKey="reports.shelf" />
          </div>
        </div>
      </div>

      <ClinicTabs
        clinics={clinics}
        active={extraTab ?? activeClinic}
        onSelect={(code) => { setClinic(code); setExtraTab(null); }}
        after={[
          // Empty Management tab hidden for everyone until it has content
          // (owner, 2026-10-06 — see SHOW_EMPTY_MANAGEMENT_TABS).
          ...(SHOW_EMPTY_MANAGEMENT_TABS && canSeeManagementTab() ? [{ id: MANAGEMENT_TAB_ID, label: 'Management' }] : []),
          { id: EXPECTED_REPORTS_TAB_ID, label: 'Expected reports' },
        ]}
        // ClinicTabs would otherwise append its own Management tab.
        management={false}
        onSelectAfter={(id) => setExtraTab(id)}
      />

      {extraTab === MANAGEMENT_TAB_ID && SHOW_EMPTY_MANAGEMENT_TABS ? (
        <ManagementPlaceholder section="Reports" />
      ) : extraTab === EXPECTED_REPORTS_TAB_ID ? (
        <ReportScheduleBoard clinic={activeClinic} />
      ) : board}
    </div>
  );
}
